import { Box, Button, Card, Divider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NextBillPaymentComponent from 'src/components/stripe/nextBill-payment.component';
import HttpServices from 'src/services/httpService';
import { paymentModuleUrl } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const NextPlanBillDetails = ({
  hasSubscription,
  hasTrailPeriod,
  expireDate,
  isAppsumoUser,
  defaultPlanName,
  paidPlanName,
  handleBuyPlan,
  isAutoPayEnabled,
  stripeSubscriptionStatus,
}) => {
  const { t: translate } = useTranslation();
  const orgDetails = CommonHelper.GetOrgDetails();
  const [isPaymentOn, setPaymentOn] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [expiresOn, setPlanExpiresOn] = useState('');
  const [lifeTimePlanName, setLifeTimePlanName] = useState('');
  const [newExpiresOn, setNewPlanExpiresOn] = useState('');
  // const [hasActiveSubscription, setActiveSubscription] = useState(false);
  const { showToast } = useToaster();
  const normalizedDefaultPlanName = String(defaultPlanName || '').toLowerCase();
  const normalizedPaidPlanName = String(paidPlanName || '').toLowerCase();
  const hasPaidPlan =
    hasSubscription ||
    (!!normalizedPaidPlanName && normalizedPaidPlanName !== 'free' && normalizedPaidPlanName !== 'free plan');
  const hasLifetimePlan =
    Boolean(isAppsumoUser) || normalizedDefaultPlanName.includes('lifetime') || normalizedPaidPlanName.includes('lifetime');
  const showNoActiveSubscriptionMessage = !hasPaidPlan && !hasTrailPeriod && !hasLifetimePlan;

  useEffect(() => {
    if (hasSubscription && !isAutoPayEnabled) {
      getNextPriceBill();
    }
    if (isAppsumoUser) {
      switch (defaultPlanName) {
        case 'brand_lifetime':
          setLifeTimePlanName('Brand');
          break;
        case 'agency_lifetime':
          setLifeTimePlanName('Agency');
          break;
        case 'mini_agency_lifetime':
          setLifeTimePlanName('Agency');
          break;
        case 'business_lifetime':
          setLifeTimePlanName('Business');
          break;
        case 'team_lifetime':
          setLifeTimePlanName('Team');
          break;
        default:
          break;
      }
    }
  }, []);

  const handlePayment = (show) => {
    setPaymentOn(show);
  };
  const failureCallback = (message) => {
    message && showToast(message);
  };

  const successCallback = (data, message) => {
    setTotalAmount(data.amount);
    setPlanExpiresOn(data.expire_date);
    setNewPlanExpiresOn(data.new_expire_date);
    // setActiveSubscription(data.has_active_subscription);
  };

  const getNextPriceBill = () => {
    let postProps = {
      url: paymentModuleUrl.getForNextBill,
      successCallback: successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  return (
    <Box sx={{}}>
      {hasTrailPeriod && !isAppsumoUser ? (
        <Box sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {translate('billing.subscription_info.currently_on_trail_plan')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.secondary', mt: 1 }}>
            {translate('billing.subscription_info.expires_on')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
            {CommonHelper.formatDateAndTime(expireDate, 'date')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 2, color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleBuyPlan}
          >
            {translate('billing.subscription_info.click_to_buy_plan')}
          </Typography>
        </Box>
      ) : (
        <>
          {isAppsumoUser && (
            <Box sx={{ padding: '5px', margin: '1rem', mb: 0 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {`${translate('billing.subscription_info.lifetime_subscription_of')} ${lifeTimePlanName} ${translate(
                  'common.plan'
                )}`}
                .
              </Typography>
            </Box>
          )}
          {hasSubscription && !isAppsumoUser && isAutoPayEnabled ? (
            <Card
              sx={{
                p: 2,
                mt: 1,
                width: '100%',
                borderRadius: 1,
                border: '1px solid',
                borderColor: stripeSubscriptionStatus === 'past_due' ? 'warning.light' : 'success.light',
                bgcolor: stripeSubscriptionStatus === 'past_due' ? 'warning.lighter' : 'success.lighter',
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                {translate('billing.subscription_info.next_bill_autopay_title', {
                  defaultValue: 'Next bill is handled by autopay',
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate('billing.subscription_info.next_bill_autopay_help', {
                  defaultValue:
                    'No manual next-month payment is needed. Stripe will automatically charge the saved card for each monthly invoice.',
                })}
              </Typography>
              {stripeSubscriptionStatus === 'past_due' && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontWeight: 700 }}>
                  {translate('billing.subscription_info.autopay_payment_attention', {
                    defaultValue:
                      'Autopay needs attention. Please update the card or retry payment from Stripe if the latest invoice failed.',
                  })}
                </Typography>
              )}
            </Card>
          ) : hasSubscription && !isAppsumoUser ? (
            <Card
              sx={{
                p: 1,
                mt: 1,
                width: '100%',
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                {translate('billing.subscription_info.current_plan_pay_to_next_month')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {translate('billing.subscription_info.expires_on')}:
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {CommonHelper.formatDateAndTime(expiresOn, 'date')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>New Expires On:</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {CommonHelper.formatDateAndTime(newExpiresOn, 'date')}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>{translate('billing.subscription_info.total_amount')}:</Typography>
                <Typography>{orgDetails.currency_symbol + totalAmount}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                {hasSubscription && (
                  <Button variant="contained" onClick={() => handlePayment(true)} sx={{ mb: 0.5 }}>
                    {translate('billing.subscription_info.pay_next_bill')}
                  </Button>
                )}
              </Box>
              {isPaymentOn && (
                <NextBillPaymentComponent
                  handleClose={handlePayment}
                  amount={totalAmount}
                  currencySymbol={orgDetails.currency_symbol}
                />
              )}
            </Card>
          ) : (
            showNoActiveSubscriptionMessage && (
              <>
                <Box sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {translate('billing.subscription_info.dont_have_any_active_subscription')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: 'primary.main',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={handleBuyPlan}
                  >
                    {translate('billing.subscription_info.click_to_buy_plan')}
                  </Typography>
                </Box>
              </>
            )
          )}
        </>
      )}
    </Box>
  );
};

export default NextPlanBillDetails;
